import Header from "../../../atoms/Header";
import DashboardTemplate from "../../../templates/Dashboard";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  NeedHelp,
  ConstructionIcon,
  CustomIcon,
  EnhancementsIcon,
  HolidayLightingIcon,
  IrrigationIcon,
  LandscapeIcon,
  LawnCareIcon,
  SnowServiceIcon,
  TreeServiceIcon,
  DeleteIcon,
} from "../../../atoms/CustomIcons";
import { PopUp } from "../../../atoms/PopUp";
import ConfirmDelete from "../../../atoms/ConfirmDelete";
import Filters from "./Components/Filters";
import { Avatar, Button, Card, Col, Dropdown, Image, Row, Tag } from "antd";
import { useAppDispatch, useAppSelector } from "../../../config/hooks";
import {
  getServiceLineListing,
  removeServiceLineListing,
} from "./ducks/actions";
import { ServiceLineListingType } from "./ducks/types";
import { deleteServiceLine, updateServiceLineStatus } from "./ducks/services";
import { AxiosError } from "axios";
import ErrorMessage from "../../../atoms/Toasts/ErrorMessage";
import SuccessMessage from "../../../atoms/Toasts/SuccessMessage";
import EmptyCard from "../../../atoms/EmptyCard";
import { MoreOutlined } from "@ant-design/icons";

export default function Services() {
  const url = useLocation();
  const companyId = url?.pathname.split("/")[3];
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [serviceRecord, setServiceRecord] =
    useState<ServiceLineListingType | null>(null);
  const [isAllDeleteModalOpen, setIsAllDeleteModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [archivePopUp, setArchivePopUp] = useState(false);
  const [dataSource, setDataSource] = useState<ServiceLineListingType[]>([]);
  const [value, setValue] = useState("");
  const [archived, showArchived] = useState(false);

  const dropdownRef = useRef<HTMLButtonElement>(null);
  const serviceLineListing = useAppSelector(
    (state) => state?.services?.serviceLineListing
  );

  const [filters, setFilters] = useState<{
    status: string;
  }>({
    status: "active",
  });

  const deleteFunction = () => {
    setLoading(true);
    deleteServiceLine([serviceRecord?.id || ""])
      .then(() => {
        setLoading(false);
        setIsDeleteModalOpen(false);
        setIsAllDeleteModalOpen(false);
        showArchived(false);
        const payload = {
          status: "active", //active,archived
          company_id: companyId,
        };
        dispatch(getServiceLineListing(payload));
        SuccessMessage("Service line deleted");
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const archiveFunction = () => {
    setLoading(true);
    const payload = {
      status: serviceRecord?.status === "active" ? "archived" : "active",
    };
    updateServiceLineStatus(serviceRecord?.id as string, payload)
      .then(() => {
        setLoading(false);
        setArchivePopUp(false);

        const payload = {
          status: serviceRecord?.status ?? "active", //active,archived
          company_id: companyId,
        };
        dispatch(getServiceLineListing(payload));
        SuccessMessage(
          `Service ${
            serviceRecord?.status === "active" ? "archived" : "activated"
          } .`
        );
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const confirmArchive = {
    visibility: archivePopUp,
    content: (
      <ConfirmDelete
        title=""
        isArchivePopup={true}
        onClose={() => setArchivePopUp(false)}
        heading="Archive service line"
        paragraph={
          <>
            Are you sure you want to archive <b>{serviceRecord?.name}</b>?<br />
            This service line won’t be available for use.
          </>
        }
        btnText="Archive service line"
        btnApi={archiveFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setArchivePopUp(false),
  };

  const confirmDeletePopUp = {
    visibility: isDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          <>
            Are you sure you want to delete <b>{serviceRecord?.name}</b>?<br />
            This action cannot be undone.
          </>
        }
        btnText="Delete service line"
        btnApi={deleteFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalOpen(false),
  };

  const deleteAllUsersPopUp = {
    visibility: isAllDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsAllDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          <>
            Are you sure you want to delete the selected services? <br />
            This action cannot be undone.
          </>
        }
        btnText="Delete services"
        btnApi={deleteFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsAllDeleteModalOpen(false),
  };

  const items = [
    {
      key: "1",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/edit-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
            navigate(
              `/company-settings/edit-service-line/${serviceRecord?.id}/${companyId}`
            );
          }}
        >
          Edit details
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={
            <Image
              src={`/images/table/${
                serviceRecord?.status === "active" ? "archive" : "activate"
              }-icon.svg`}
              preview={false}
            />
          }
          onClick={(e) => {
            e.stopPropagation();
            serviceRecord?.status === "active"
              ? setArchivePopUp(true)
              : archiveFunction();

            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          {serviceRecord?.status === "active" ? "Archive" : "Activate"}
        </Button>
      ),
    },
    {
      key: "3",
      label: (
        <Button
          type="text"
          className="delete-hover"
          icon={<DeleteIcon />}
          onClick={(e) => {
            setIsDeleteModalOpen(true);
            e.stopPropagation();
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          Delete
        </Button>
      ),
    },
  ];

  const iconComponentMap: Record<string, React.ReactNode> = {
    LandscapeIcon: <LandscapeIcon />,
    EnhancementsIcon: <EnhancementsIcon />,
    LawnCareIcon: <LawnCareIcon />,
    IrrigationIcon: <IrrigationIcon />,
    ConstructionIcon: <ConstructionIcon />,
    HolidayLightingIcon: <HolidayLightingIcon />,
    SnowServiceIcon: <SnowServiceIcon />,
    TreeServiceIcon: <TreeServiceIcon />,
    CustomIcon: <CustomIcon />,
  };

  useEffect(() => {
    if (companyId) {
      const payload = {
        status: filters?.status, //active,archived
        company_id: companyId,
      };
      dispatch(getServiceLineListing(payload));
    }
  }, [companyId, dispatch, filters]);

  useEffect(() => {
    const currentData = serviceLineListing?.data;
    let filteredData: ServiceLineListingType[] = [];

    if (value && currentData) {
      filteredData = currentData?.filter((entry) => {
        const typedData = value
          ? entry?.name && entry?.name.toLowerCase().includes(value)
          : true;

        return typedData;
      });

      setDataSource(filteredData);
    } else {
      setDataSource(serviceLineListing?.data ?? []);
    }
  }, [value, serviceLineListing]);

  useEffect(() => {
    dispatch(removeServiceLineListing());
  }, [dispatch]);

  return (
    <DashboardTemplate loading={loading || serviceLineListing?.loader}>
      <Header
        breadcrumb={[
          { label: "Company settings", href: `/company-settings/${companyId}` },
          { label: "Services" },
        ]}
        title="Service lines"
        description="Manage your company’s service lines and services."
        btnText1="Need help?"
        btnIcon1={<NeedHelp />}
      />

      <Filters
        setFilters={setFilters}
        companyId={companyId}
        value={value}
        setValue={setValue}
        archived={archived}
        showArchived={showArchived}
      />

      <div className="services_cards">
        <Row gutter={[12, 12]} style={{ alignItems: "stretch" }}>
          {dataSource &&
            dataSource?.length > 0 &&
            dataSource?.map((data) => (
              <Col span={8} key={data?.id}>
                <Card
                  title={
                    <Avatar
                      className={`${data?.color} defaultColor`}
                      src={iconComponentMap[data?.icon]}
                    />
                  }
                  onClick={() =>
                    navigate(
                      `/company-settings/service-lines/${data?.id}/${companyId}`
                    )
                  }
                  style={{
                    cursor: "pointer",
                    borderRadius: "16px",
                    height: "100%",
                  }}
                  extra={
                    <Dropdown
                      menu={{
                        items,
                      }}
                      trigger={["click"]}
                      placement="bottomRight"
                      className="actionButton"
                    >
                      <Button
                        onClick={(e) => {
                          setServiceRecord(data);
                          e.stopPropagation();
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{
                          padding: "6px !important",
                          height: "auto",
                          background: "transparent",
                        }}
                        className="only_icon"
                        icon={<MoreOutlined />}
                        ref={dropdownRef}
                      />
                    </Dropdown>
                  }
                >
                  <h3>{data?.name}</h3>
                  {data?.services && data?.services?.length > 0 && (
                    <div className="services_tags">
                      {data?.services?.map((e, i) => (
                        <Tag
                          className="large-tag default_tag"
                          key={i}
                          style={{ background: "#fff" }}
                        >
                          {e?.name}
                        </Tag>
                      ))}
                      {data?.service_count > 4 && (
                        <Tag
                          className="large-tag default_tag"
                          style={{ background: "#fff" }}
                        >
                          +{data?.service_count - 4}...
                        </Tag>
                      )}
                    </div>
                  )}
                </Card>
              </Col>
            ))}

          {dataSource && dataSource?.length === 0 && (
            <div className="empty_wrap">
              <EmptyCard
                imageHeight={220}
                title={
                  archived
                    ? "There are no archived service lines"
                    : value?.length > 0
                    ? "No results match the filter or search criteria"
                    : "No service line added yet"
                }
                description={
                  archived
                    ? "We believe in our absolute success and hope to never have an archived service line here."
                    : value?.length > 0
                    ? "Try adjusting the filters and search."
                    : "Set up services for Landscape maintenance by defining work details, production rates, materials, and equipment."
                }
                image={
                  archived || value?.length > 0
                    ? "/images/empty-icons/no-archived.svg"
                    : "/images/empty-icons/error-icon.svg"
                }
              />
            </div>
          )}
        </Row>
      </div>

      <PopUp {...confirmArchive} />
      <PopUp {...confirmDeletePopUp} />
      <PopUp {...deleteAllUsersPopUp} />
    </DashboardTemplate>
  );
}
